/**
 * Pure calculation functions for 1031 exchange boot netting and tax analysis.
 * All IRS rules implemented per IRC §1031 and Treasury Reg. §1.1031.
 */

// --- Types ---

export interface BootNettingInput {
  cashReceivedAtClosing: number
  cashPaidAtClosing: number
  oldMortgagePayoff: number
  newMortgageAmount: number
}

export interface BootNettingResult {
  netCash: number
  cashBoot: number
  excessCashPaid: number
  netMortgageRelief: number
  mortgageBoot: number
  taxableMortgageBoot: number
  totalBoot: number
}

export interface TaxInput {
  gain: number
  accumulatedDepreciation: number
  filingStatus: 'single' | 'married_joint' | 'married_separate' | 'head_of_household'
  estimatedMAGI: number
  ltcgRate: 15 | 20
  stateRate: number // percentage, e.g. 9.3 for California
}

export interface TaxBreakdown {
  depreciationRecapture: number
  depreciationRecaptureRate: number
  remainingGain: number
  ltcgTax: number
  ltcgRate: number
  niitTax: number
  niitApplies: boolean
  niitThreshold: number
  stateTax: number
  stateRate: number
  totalTax: number
  effectiveRate: number
}

export interface ExchangeCalculationInput {
  // Property values
  originalCost: number
  accumulatedDepreciation: number
  capitalImprovements: number
  sellingPrice: number
  sellingExpenses: number
  replacementPropertyCost: number
  // Mortgage
  oldMortgagePayoff: number
  newMortgageAmount: number
  // Cash
  cashPaidAtClosing: number
  cashReceivedAtClosing: number
  // Tax
  filingStatus: 'single' | 'married_joint' | 'married_separate' | 'head_of_household'
  estimatedMAGI: number
  ltcgRate: 15 | 20
  stateRate: number
}

export interface ExchangeCalculationResult {
  // Basis
  adjustedBasis: number
  // Gain
  realizedGain: number
  // Boot
  boot: BootNettingResult
  // Recognized vs deferred
  recognizedGain: number
  deferredGain: number
  // Replacement basis
  replacementPropertyBasis: number
  // Tax breakdown — what you'd owe WITHOUT the exchange
  taxWithout1031: TaxBreakdown
  // Tax on recognized gain only (boot)
  taxOn1031: TaxBreakdown
  // Net savings
  taxSavings: number
}

// --- NIIT thresholds ---

const NIIT_THRESHOLDS: Record<string, number> = {
  single: 200000,
  married_joint: 250000,
  married_separate: 125000,
  head_of_household: 200000,
}

// --- Boot Netting (IRS Asymmetric Rules) ---

export function calculateBootNetting(input: BootNettingInput): BootNettingResult {
  // Step 1: Net Cash
  const netCash = input.cashReceivedAtClosing - input.cashPaidAtClosing
  const cashBoot = Math.max(0, netCash)
  const excessCashPaid = Math.max(0, -netCash)

  // Step 2: Net Mortgage Relief
  // Old mortgage paid off = relief to exchanger (like receiving cash)
  // New mortgage taken on = additional liability (like paying cash)
  const netMortgageRelief = input.oldMortgagePayoff - input.newMortgageAmount
  const mortgageBoot = Math.max(0, netMortgageRelief)

  // Step 3: Offset mortgage boot with excess cash paid
  // CRITICAL: Excess cash paid CAN offset mortgage boot
  const taxableMortgageBoot = Math.max(0, mortgageBoot - excessCashPaid)

  // Step 4: Total boot
  // CRITICAL: Excess mortgage paid CANNOT offset cash boot received
  const totalBoot = cashBoot + taxableMortgageBoot

  return {
    netCash,
    cashBoot,
    excessCashPaid,
    netMortgageRelief,
    mortgageBoot,
    taxableMortgageBoot,
    totalBoot,
  }
}

// --- Multi-Rate Tax Calculation ---

export function calculateTaxBreakdown(input: TaxInput): TaxBreakdown {
  const { gain, accumulatedDepreciation, filingStatus, estimatedMAGI, ltcgRate, stateRate } = input

  if (gain <= 0) {
    return {
      depreciationRecapture: 0,
      depreciationRecaptureRate: 0.25,
      remainingGain: 0,
      ltcgTax: 0,
      ltcgRate: ltcgRate / 100,
      niitTax: 0,
      niitApplies: false,
      niitThreshold: NIIT_THRESHOLDS[filingStatus] || 200000,
      stateTax: 0,
      stateRate: stateRate / 100,
      totalTax: 0,
      effectiveRate: 0,
    }
  }

  // Depreciation recapture (Section 1250) — taxed at 25%
  const depreciationPortion = Math.min(gain, accumulatedDepreciation)
  const depreciationRecapture = depreciationPortion * 0.25

  // Remaining gain — taxed at LTCG rate
  const remainingGain = gain - depreciationPortion
  const ltcgTax = remainingGain * (ltcgRate / 100)

  // NIIT (3.8%) — applies if MAGI exceeds threshold
  const niitThreshold = NIIT_THRESHOLDS[filingStatus] || 200000
  const niitApplies = estimatedMAGI > niitThreshold
  const niitTax = niitApplies ? gain * 0.038 : 0

  // State tax
  const stateTax = gain * (stateRate / 100)

  const totalTax = depreciationRecapture + ltcgTax + niitTax + stateTax
  const effectiveRate = gain > 0 ? totalTax / gain : 0

  return {
    depreciationRecapture,
    depreciationRecaptureRate: 0.25,
    remainingGain,
    ltcgTax,
    ltcgRate: ltcgRate / 100,
    niitTax,
    niitApplies,
    niitThreshold,
    stateTax,
    stateRate: stateRate / 100,
    totalTax,
    effectiveRate,
  }
}

// --- Full Exchange Calculation ---

export function calculateExchange(input: ExchangeCalculationInput): ExchangeCalculationResult {
  // Adjusted basis
  const adjustedBasis = input.originalCost + input.capitalImprovements - input.accumulatedDepreciation

  // Realized gain (amount realized minus adjusted basis)
  const amountRealized = input.sellingPrice - input.sellingExpenses
  const realizedGain = Math.max(0, amountRealized - adjustedBasis)

  // Boot netting
  const boot = calculateBootNetting({
    cashReceivedAtClosing: input.cashReceivedAtClosing,
    cashPaidAtClosing: input.cashPaidAtClosing,
    oldMortgagePayoff: input.oldMortgagePayoff,
    newMortgageAmount: input.newMortgageAmount,
  })

  // Recognized gain = lesser of realized gain and boot received
  const recognizedGain = Math.min(realizedGain, boot.totalBoot)
  const deferredGain = realizedGain - recognizedGain

  // Replacement property basis = FMV of replacement - deferred gain
  const replacementPropertyBasis = input.replacementPropertyCost - deferredGain

  // Tax breakdown: what you'd owe on full gain WITHOUT 1031
  const taxInput: TaxInput = {
    gain: realizedGain,
    accumulatedDepreciation: input.accumulatedDepreciation,
    filingStatus: input.filingStatus,
    estimatedMAGI: input.estimatedMAGI,
    ltcgRate: input.ltcgRate,
    stateRate: input.stateRate,
  }
  const taxWithout1031 = calculateTaxBreakdown(taxInput)

  // Tax on recognized gain only (what you actually owe with the exchange)
  const taxOn1031 = calculateTaxBreakdown({
    ...taxInput,
    gain: recognizedGain,
    // Depreciation recapture applies first, so if recognized gain is small
    // it's all recapture
    accumulatedDepreciation: Math.min(recognizedGain, input.accumulatedDepreciation),
  })

  const taxSavings = taxWithout1031.totalTax - taxOn1031.totalTax

  return {
    adjustedBasis,
    realizedGain,
    boot,
    recognizedGain,
    deferredGain,
    replacementPropertyBasis,
    taxWithout1031,
    taxOn1031,
    taxSavings,
  }
}

// --- Formatting helpers ---

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}
